/**
 * Doc image upload — presign → PUT to R2 → confirm.
 * Uses the same public bucket as custom icons; keys are scoped per doc.
 */

import { pletApiRequest } from '../utils/api/client';
import {
  DOC_IMAGE_ALLOWED_TYPES,
  DOC_IMAGE_MAX_BYTES,
  validateDocImageFile,
} from '../components/DocEditor/utils/imageFiles';

/**
 * Upload a doc image file to R2.
 * @param {string} docId
 * @param {File} file
 * @param {(n: number) => void} [onProgress]
 * @returns {Promise<{ imageId: string, publicUrl: string, r2Key: string, fileName: string, mimeType: string, size: number }>}
 */
export async function uploadDocImage(docId, file, onProgress = () => {}) {
  if (!docId) throw new Error('Doc must be saved before uploading images');

  const check = validateDocImageFile(file);
  if (!check.ok) throw new Error(check.error);

  onProgress(10);

  const { uploadUrl, imageId, r2Key } = await pletApiRequest('/doc-images/upload-url', {
    method: 'POST',
    body: JSON.stringify({
      docId,
      fileName: file.name,
      mimeType: file.type,
      size: file.size,
    }),
  });

  onProgress(35);

  const uploadResponse = await fetch(uploadUrl, {
    method: 'PUT',
    headers: {
      'Content-Type': file.type,
      'Content-Length': file.size.toString(),
    },
    body: file,
  });

  if (!uploadResponse.ok) {
    throw new Error('Failed to upload image to storage');
  }

  onProgress(85);

  const result = await pletApiRequest('/doc-images/confirm', {
    method: 'POST',
    body: JSON.stringify({ imageId }),
  });

  onProgress(100);

  return {
    imageId: result.imageId,
    publicUrl: result.publicUrl,
    r2Key: result.r2Key || r2Key,
    fileName: result.fileName || file.name,
    mimeType: result.mimeType || file.type,
    size: result.size ?? file.size,
  };
}

export const DOC_IMAGE_UPLOAD_LIMITS = {
  maxBytes: DOC_IMAGE_MAX_BYTES,
  allowedTypes: DOC_IMAGE_ALLOWED_TYPES,
};
